import { create } from "zustand";
import * as db from "../lib/db";
import toast from "react-hot-toast";
import { syncAllProfilePics } from "../components/ProfilePicture";

const TEAM_KEY = "taxshop_team";
const SERVICES_KEY = "taxshop_client_services";
function loadSavedServices() {
  try { return JSON.parse(localStorage.getItem(SERVICES_KEY)) || {}; } catch { return {}; }
}
function saveClientServices(id, services) {
  const all = loadSavedServices();
  all[id] = services;
  localStorage.setItem(SERVICES_KEY, JSON.stringify(all));
  // Write directly to client row — fetchClients on any device returns it immediately
  db.setClientServices(id, services).catch(() => {});
  // Also write to app_settings as belt-and-suspenders
  db.upsertAppSetting("clientServices", all).catch(() => {});
}
const DEFAULT_TEAM = [
  { id: 1, name: "Monique", role: "Senior Accountant / Director", status: "Available", initials: "MD", activeTasks: 0, estHours: 0, capacity: 40, color: "#FCDE3D", email: "monique.d@mdgaccounting.co.za", phone: "" },
  { id: 2, name: "Cloey", role: "AI Practice Assistant", status: "Available", initials: "CL", activeTasks: 0, estHours: 0, capacity: 168, color: "#008C95", email: "", phone: "" },
];
function loadTeam() {
  try {
    const stored = JSON.parse(localStorage.getItem(TEAM_KEY));
    if (!stored) return DEFAULT_TEAM;
    // Ensure DEFAULT_TEAM members always exist (won't duplicate if already present by name)
    const names = new Set(stored.map((m) => m.name));
    const missing = DEFAULT_TEAM.filter((m) => !names.has(m.name));
    return missing.length ? [...stored, ...missing] : stored;
  } catch { return DEFAULT_TEAM; }
}

const useStore = create((set, get) => ({
  // ── Active client in focus (set when a client profile is open) ──
  activeClientName: null,
  setActiveClientName: (name) => set({ activeClientName: name }),
  activeTab: null,
  setActiveTab: (tab) => set({ activeTab: tab }),

  // ── Data ──
  clients: [],
  tasks: [],
  deadlines: [],
  comms: [],
  notes: [],
  leads: [],
  engagementLetters: [],
  checkedItems: {},
  uploadedDocs: [],
  docs: {
    "Bookkeeping": ["Bank Recon – May 2026", "Trial Balance – May 2026", "Cashbook – May 2026", "Supplier Invoices Folder"],
    "Financial Statements": ["AFS Draft – FY2025", "Management Accounts – Apr 2026", "Working Papers FY2025"],
    "Tax Returns": ["ITR12 – Individual Income Tax 2025", "ITR14 – Company Income Tax 2025", "IRP6 – 1st Provisional 2025", "IRP6 – 2nd Provisional 2026", "IT34 – Assessment Notice", "VAT201 Return"],
    "Payroll": ["Payslips – May 2026", "EMP201 – May 2026", "IRP5 Certificates 2025", "UIF Declaration – May 2026"],
    "Compliance": ["EMP501 Recon – Feb 2026", "Beneficial Ownership Register", "CIPC Annual Return"],
    "Statutory & Legal": ["CoR14.3 – Company Registration", "MOI", "Share Register", "Directors Register", "Founding Resolutions"],
    "FICA & KYC": ["ID Document – Director/Member", "Proof of Address", "Source of Funds Declaration", "Beneficial Ownership Register"],
    "Client Onboarding": ["Signed Engagement Letter", "Mandate – Tax Services", "Client Info Form"],
  },
  checklist: [
    { cat: "Company Documents", items: ["MOI", "CoR14.3 Registration Certificate", "Share Certificates", "Founding Resolutions", "Share Register", "Directors Register", "Annual Board Minutes / Resolutions", "Employment Equity Plan on file", "Workplace Skills Plan on file", "BCEA poster displayed", "Employment Equity Report submitted (if applicable)"] },
    { cat: "Tax Registrations (SARS)", items: ["Income Tax Registration (IT)", "VAT Registration", "PAYE Registration", "UIF Registration", "SDL Registration", "COIDA Registration"] },
    { cat: "FICA / KYC", items: ["ID Documents – Directors / Members", "Proof of Address (not older than 3 months)", "Beneficial Ownership Register", "Source of Funds Declaration"] },
    { cat: "Engagement", items: ["Signed Engagement Letter", "Signed Tax Mandate", "Signed Payroll Mandate", "Fee Agreement / Quote Accepted"] },
    { cat: "Xero Setup", items: ["Xero subscription activated", "Chart of accounts configured", "Bank feeds connected", "Opening balances entered", "Payroll module configured", "VAT settings configured", "Reporting layouts set up", "Client trained on Xero"] },
    { cat: "Handover (Previous Accountant)", items: ["Handover letter sent to previous accountant", "Previous accountant confirmed handover", "Historical trial balances received", "Previous year tax returns received", "SARS correspondence received", "Xero/accounting data exported and imported"] },
  ],
  team: loadTeam(),

  // ── Loading ──
  loading: true,
  error: null,

  // ── Bootstrap ──
  loadAll: async () => {
    set({ loading: true, error: null });
    const withTimeout = (p, ms = 20000) => Promise.race([p, new Promise((_, rej) => setTimeout(() => rej(new Error("Request timed out — check your internet connection and try again.")), ms))]);
    try {
      // fetchAllAppSettings loaded lazily — avoids blocking boot if slow (54MB+ table)
      const appSettingsPromise = db.fetchAllAppSettings().catch(() => ({}));
      const [clients, tasks, deadlines, comms, notes, leads, engagementLetters, checkedItems] = await withTimeout(Promise.all([
        db.fetchClients(),
        db.fetchTasks(),
        db.fetchDeadlines(),
        db.fetchComms(),
        db.fetchNotes(),
        db.fetchLeads(),
        db.fetchEngagementLetters(),
        db.fetchChecklist(),
      ]));
      // Documents and app_settings loaded lazily to avoid blocking boot
      db.fetchDocuments().then(docs => set({ uploadedDocs: docs })).catch(() => {});
      const appSettings = await appSettingsPromise.catch(() => ({}));
      // Services — three-tier priority (highest to lowest):
      //   1. app_settings.clientServices (Supabase, written by any device when saving services)
      //   2. c.services from fetchClients (Supabase client row, written by setClientServices)
      //   3. localStorage (this device only, fallback)
      const localSvc = loadSavedServices();
      const remoteSvc = appSettings.clientServices || {};
      const mergedSvc = { ...localSvc, ...remoteSvc };
      localStorage.setItem(SERVICES_KEY, JSON.stringify(mergedSvc));
      // ONE-TIME MIGRATION: if app_settings has no clientServices but localStorage does, push it up
      if (!appSettings.clientServices && Object.keys(localSvc).length > 0) {
        db.upsertAppSetting("clientServices", localSvc).catch(() => {});
        Object.entries(localSvc).forEach(([cid, svcs]) => db.setClientServices(cid, svcs).catch(() => {}));
      }
      // Hydrate: app_settings/localStorage first, then client-row services, then empty
      const hydratedClients = clients.map(c => ({
        ...c,
        // Union both sources: app_settings wins for items it knows about, but client-row additions are never dropped.
        // This ensures a service added directly to the Supabase clients row (e.g. "sales_orders") is always visible.
        services: [...new Set([...(mergedSvc[c.id] || []), ...(c.services || [])])],
      }));
      // Team: prefer Supabase app_settings, fall back to localStorage
      // Merge DEFAULT_TEAM contact details (email/phone) into loaded team if missing
      let team = appSettings.team || loadTeam();
      let teamUpdated = false;
      team = team.map(m => {
        const def = DEFAULT_TEAM.find(d => d.name === m.name);
        if (def && (!m.email && def.email)) { teamUpdated = true; return { ...m, email: def.email, phone: m.phone ?? def.phone ?? "" }; }
        return m;
      });
      if (teamUpdated) db.upsertAppSetting("team", team).catch(() => {});
      // Sync profile pics from Supabase Storage into localStorage (non-blocking — covers all devices)
      syncAllProfilePics(team.map(m => m.name)).catch(() => {});
      // Todos: prefer Supabase, fall back to localStorage
      const todos = appSettings.todos ?? get().todos;
      // Service labels: prefer Supabase, fall back to localStorage
      const serviceLabels = appSettings.serviceLabels ?? null; // null = use hook's own localStorage
      // Settings: prefer Supabase, fall back to localStorage
      const practiceSettings = appSettings.practiceSettings ?? null;
      const appToggles = appSettings.appToggles ?? null;
      const calendarEvents = appSettings.calendarEvents ?? null;
      // Sync back to localStorage so hooks that read localStorage still work
      if (team) localStorage.setItem(TEAM_KEY, JSON.stringify(team));
      if (todos) localStorage.setItem("taxshop_mytodos", JSON.stringify(todos));
      if (serviceLabels) localStorage.setItem("taxshop_service_labels", JSON.stringify(serviceLabels));
      if (practiceSettings) {
        // Logos are stored in a separate key — never in the main practice blob.
        const { logo: _l, portalLogo: _pl, ...settingsNoLogos } = practiceSettings;
        try { localStorage.setItem("taxshop_practice", JSON.stringify(settingsNoLogos)); } catch {}
        // Merge logos back in for the dispatched event so UI picks them up.
        let logos = {};
        try { logos = JSON.parse(localStorage.getItem("taxshop_practice_logos") || "{}"); } catch {}
        const detail = { ...settingsNoLogos, ...logos };
        window.dispatchEvent(new CustomEvent("taxshop:practice-updated", { detail }));
      }
      if (appToggles) localStorage.setItem("taxshop_toggles", JSON.stringify(appToggles));
      if (calendarEvents) localStorage.setItem("taxshop_cal_events", JSON.stringify(calendarEvents));
      const workpapers = appSettings.workpapers ?? null;
      if (workpapers) {
        // Try full write first — desktop handles it fine.
        // Only strip base64 logos if quota is exceeded (mobile 5MB limit).
        // Storage URLs (non-data: strings) are always kept.
        try {
          localStorage.setItem("taxshop_workpapers", JSON.stringify(workpapers));
        } catch (qe) {
          if (qe.name === "QuotaExceededError" || qe.code === 22) {
            const stripped = Object.fromEntries(
              Object.entries(workpapers).map(([name, data]) => {
                const logo = data?.branding?.logo;
                if (!logo || !logo.startsWith("data:")) return [name, data];
                const { logo: _logo, ...brandingRest } = data.branding;
                return [name, { ...data, branding: brandingRest }];
              })
            );
            try {
              localStorage.setItem("taxshop_workpapers", JSON.stringify(stripped));
            } catch {
              // Last resort: drop all branding
              try {
                const minimal = Object.fromEntries(
                  Object.entries(workpapers).map(([name, data]) => [name, { ...data, branding: undefined }])
                );
                localStorage.setItem("taxshop_workpapers", JSON.stringify(minimal));
              } catch { /* give up — app reads from Supabase directly */ }
            }
          }
        }
      }
      const pinnedMap = appSettings.pinnedMap ?? null;
      if (pinnedMap) {
        localStorage.setItem("taxshop_pinned", JSON.stringify(pinnedMap));
        window.dispatchEvent(new CustomEvent("taxshop:pin-updated"));
      }
      set({ clients: hydratedClients, tasks, deadlines, comms, notes, leads, engagementLetters, checkedItems, team, todos, loading: false });
    } catch (e) {
      // QuotaExceededError is a localStorage limit — not a real DB failure, don't block the app
      if (e.name === "QuotaExceededError" || e.code === 22) {
        console.warn("localStorage quota exceeded during boot — continuing without local cache");
        set({ loading: false });
        return;
      }
      console.error("Failed to load data:", e);
      set({ error: e.message, loading: false });
    }
  },

  // ── Documents ──
  addUploadedDoc: (doc) => set((s) => ({ uploadedDocs: [doc, ...s.uploadedDocs] })),
  removeUploadedDoc: (id) => set((s) => ({ uploadedDocs: s.uploadedDocs.filter((d) => d.id !== id) })),
  refreshUploadedDocs: async () => {
    try {
      const docs = await db.fetchDocuments();
      set({ uploadedDocs: docs });
    } catch {}
  },

  // ── Clients ──
  addClient: async (client) => {
    try {
      const created = await db.createClient(client);
      set((s) => ({ clients: [...s.clients, created] }));
      toast.success(`${created.name} added successfully`);
      return created;
    } catch (e) {
      toast.error(`Failed to add client: ${e.message}`);
      throw e;
    }
  },
  updateClient: async (id, patch) => {
    try {
      const updated = await db.updateClient(id, patch);
      if (patch.services !== undefined) saveClientServices(id, patch.services);
      set((s) => ({
        clients: s.clients.map((c) => {
          if (c.id !== id) return c;
          return {
            ...c,
            ...updated,
            // Explicitly preserve JSON blob fields that updateClient writes but normaliseClient
            // may return as null when the DB returns the full row without those columns selected.
            onboardingState:  patch.onboardingState  !== undefined ? patch.onboardingState  : (updated.onboardingState  ?? c.onboardingState),
            docChecklist:     patch.docChecklist      !== undefined ? patch.docChecklist      : (updated.docChecklist      ?? c.docChecklist),
            complianceCustom: patch.complianceCustom  !== undefined ? patch.complianceCustom  : (updated.complianceCustom  ?? c.complianceCustom),
            piScoreData:      patch.piScoreData        !== undefined ? patch.piScoreData        : (updated.piScoreData        ?? c.piScoreData),
            services: patch.services !== undefined ? patch.services : c.services,
            contacts: patch.contacts !== undefined ? (updated.contacts ?? patch.contacts) : c.contacts,
            engagementLetters: c.engagementLetters,
          };
        }),
      }));
    } catch (e) {
      toast.error(`Save failed: ${e.message}`);
      throw e;
    }
  },
  deleteClient: async (id) => {
    try {
      await db.deleteClient(id);
      set((s) => ({
        clients:     s.clients.filter((c) => c.id !== id),
        tasks:       s.tasks.filter((t) => Number(t.clientId) !== Number(id)),
        deadlines:   s.deadlines.filter((d) => Number(d.clientId) !== Number(id)),
        notes:       s.notes.filter((n) => Number(n.clientId) !== Number(id)),
        comms:       s.comms.filter((x) => Number(x.clientId) !== Number(id)),
        uploadedDocs: s.uploadedDocs.filter((d) => Number(d.clientId) !== Number(id)),
      }));
      toast.success("Client permanently deleted");
    } catch (e) {
      toast.error(`Delete failed: ${e.message}`);
      throw e;
    }
  },

  // ── Tasks ──
  addTask: async (task) => {
    try {
      const created = await db.createTask(task);
      set((s) => ({ tasks: [...s.tasks, created] }));
      toast.success(`✓ "${created.title}" saved to database`);
    } catch (e) {
      toast.error(`Error saving task — ${e.message}`, { duration: 6000 });
    }
  },
  updateTask: async (id, patch) => {
    try {
      const updated = await db.updateTask(id, patch);
      set((s) => ({ tasks: s.tasks.map((t) => (t.id === id ? { ...t, ...updated } : t)) }));
      // Only toast for explicit status changes or title edits, not silent checklist saves
      if (patch.status === "Done") toast.success("✓ Task marked Done");
      else if (patch.title !== undefined) toast.success("✓ Task updated");
    } catch (e) {
      toast.error(`Error saving task — ${e.message}`, { duration: 6000 });
    }
  },
  deleteTask: async (id) => {
    try {
      await db.deleteTask(id);
      set((s) => ({ tasks: s.tasks.filter((t) => t.id !== id) }));
      toast.success("Task deleted");
    } catch (e) {
      toast.error(`Error deleting task — ${e.message}`, { duration: 6000 });
    }
  },

  // ── Deadlines ──
  addDeadline: async (dl) => {
    try {
      const created = await db.createDeadline(dl);
      set((s) => ({ deadlines: [...s.deadlines, created] }));
      toast.success("Deadline added");
    } catch (e) {
      toast.error(`Failed to add deadline: ${e.message}`);
      throw e;
    }
  },
  updateDeadline: async (id, patch) => {
    try {
      const updated = await db.updateDeadline(id, patch);
      set((s) => ({ deadlines: s.deadlines.map((d) => (d.id === id ? { ...d, ...updated } : d)) }));
      toast.success("Deadline updated");
    } catch (e) {
      toast.error(`Failed to update deadline: ${e.message}`);
    }
  },
  deleteDeadline: async (id) => {
    try {
      await db.deleteDeadline(id);
      set((s) => ({ deadlines: s.deadlines.filter((d) => d.id !== id) }));
      toast.success("Deadline removed");
    } catch (e) {
      toast.error(`Failed to delete deadline: ${e.message}`);
    }
  },

  // ── Notes ──
  addNote: async (note) => {
    // Destructure portal fields before passing note to DB (DB doesn't have these columns)
    const { sendToPortal, portalCategory, ...noteData } = note;
    try {
      const created = await db.createNote(noteData);
      set((s) => ({ notes: [created, ...s.notes] }));
      toast.success(`✓ Note "${created.subject}" saved`);
      // Auto-log to client communications tab if note is linked to a client
      if (created.clientId) {
        try {
          const comm = await db.createComm({
            clientId: created.clientId,
            type: "Note",
            note: `📝 Note added: "${created.subject}"${created.body ? ` — ${created.body.slice(0, 120)}${created.body.length > 120 ? "…" : ""}` : ""}`,
            date: created.date || new Date().toISOString().slice(0, 10),
            userName: "Monique",
          });
          set((s) => ({ comms: [comm, ...s.comms] }));
        } catch {
          // Comm log is best-effort — don't fail the note save
        }
        // Send to client portal if requested
        if (sendToPortal) {
          try {
            const portalContent = [created.subject, created.body].filter(Boolean).join("\n\n");
            await db.sendPortalMessage({
              clientId: created.clientId,
              content: portalContent,
              category: portalCategory || "general",
            });
            toast.success("✓ Also sent to client portal");
          } catch {
            toast.error("Note saved, but portal send failed — check portal tab");
          }
        }
      }
      return created;
    } catch (e) {
      toast.error(`Failed to save note: ${e.message}`);
      return null;
    }
  },
  updateNote: async (id, patch) => {
    try {
      const { sendToPortal, portalCategory, ...dbPatch } = patch;
      const updated = await db.updateNote(id, dbPatch);
      set((s) => ({ notes: s.notes.map((n) => (n.id === id ? { ...n, ...updated } : n)) }));
      // Auto-log status changes to client communications
      if (patch.status && updated.clientId) {
        try {
          const comm = await db.createComm({
            clientId: updated.clientId,
            type: "Note",
            note: `📝 Note "${updated.subject}" marked as ${patch.status}`,
            date: new Date().toISOString().slice(0, 10),
            userName: "Monique",
          });
          set((s) => ({ comms: [comm, ...s.comms] }));
        } catch {
          // Best-effort
        }
      }
    } catch (e) {
      toast.error(`Failed to update note: ${e.message}`);
    }
  },
  deleteNote: async (id) => {
    try {
      await db.deleteNote(id);
      set((s) => ({ notes: s.notes.filter((n) => n.id !== id) }));
      toast.success("Note deleted");
    } catch (e) {
      toast.error(`Failed to delete note: ${e.message}`);
    }
  },

  // ── Communications ──
  addComm: async (comm) => {
    try {
      const created = await db.createComm(comm);
      set((s) => ({ comms: [created, ...s.comms] }));
    } catch (e) {
      toast.error(`Failed to post message: ${e.message}`);
    }
  },
  deleteComm: async (id) => {
    try {
      await db.deleteComm(id);
      // remove the message AND any of its replies
      set((s) => ({ comms: s.comms.filter((c) => c.id !== id && c.parentId !== id) }));
      toast.success("Message deleted");
    } catch (e) {
      toast.error(`Failed to delete message: ${e.message}`);
    }
  },
  updateComm: async (id, patch) => {
    try {
      const updated = await db.updateComm(id, patch);
      set((s) => ({ comms: s.comms.map((c) => (c.id === id ? { ...c, ...updated } : c)) }));
    } catch (e) {
      toast.error(`Failed to update: ${e.message}`);
    }
  },

  // ── Leads ──
  addLead: async (lead) => {
    try {
      const created = await db.createLead(lead);
      set((s) => ({ leads: [...s.leads, created] }));
    } catch (e) {
      toast.error(`Failed to add lead: ${e.message}`);
    }
  },
  updateLead: async (id, patch) => {
    try {
      const updated = await db.updateLead(id, patch);
      set((s) => ({ leads: s.leads.map((l) => (l.id === id ? { ...l, ...updated } : l)) }));
    } catch (e) {
      toast.error(`Failed to update lead: ${e.message}`);
    }
  },
  deleteLead: async (id) => {
    try {
      await db.deleteLead(id);
      set((s) => ({ leads: s.leads.filter((l) => l.id !== id) }));
    } catch (e) {
      toast.error(`Failed to delete lead: ${e.message}`);
    }
  },

  // ── Engagement Letters ──
  updateEngagementLetter: async (clientId, status) => {
    try {
      const date = new Date().toISOString().slice(0, 10);
      await db.upsertEngagementLetter(clientId, status, date);
      set((s) => {
        const exists = s.engagementLetters.some((e) => e.clientId === clientId);
        return {
          engagementLetters: exists
            ? s.engagementLetters.map((e) => e.clientId === clientId ? { ...e, status, date } : e)
            : [...s.engagementLetters, { clientId, status, date }],
        };
      });
    } catch (e) {
      toast.error(`Failed to update engagement letter: ${e.message}`);
    }
  },

  // ── Checklist ──
  toggleChecked: async (key) => {
    const current = get().checkedItems[key];
    const next    = !current;
    set((s) => ({ checkedItems: { ...s.checkedItems, [key]: next } }));
    try {
      await db.toggleChecklistItem(key, next);
    } catch (e) {
      // revert on failure
      set((s) => ({ checkedItems: { ...s.checkedItems, [key]: current } }));
      toast.error("Failed to save checklist item");
    }
  },

  // ── Team (localStorage + Supabase) ──
  updateTeamMember: (id, patch) => {
    set((s) => {
      const team = s.team.map((m) => (m.id === id ? { ...m, ...patch } : m));
      localStorage.setItem(TEAM_KEY, JSON.stringify(team));
      db.upsertAppSetting("team", team).catch(() => {});
      return { team };
    });
  },
  addTeamMember: (member) => {
    const id = Date.now();
    set((s) => {
      const team = [...s.team, { id, activeTasks: 0, ...member }];
      localStorage.setItem(TEAM_KEY, JSON.stringify(team));
      db.upsertAppSetting("team", team).catch(() => {});
      return { team };
    });
  },
  removeTeamMember: (id) => {
    set((s) => {
      const team = s.team.filter((m) => m.id !== id);
      localStorage.setItem(TEAM_KEY, JSON.stringify(team));
      db.upsertAppSetting("team", team).catch(() => {});
      return { team };
    });
  },

  // ── My To-Do List (localStorage + Supabase) ──
  todos: (() => { try { return JSON.parse(localStorage.getItem("taxshop_mytodos") || "[]"); } catch { return []; } })(),
  _saveTodos: (todos) => {
    localStorage.setItem("taxshop_mytodos", JSON.stringify(todos));
    db.upsertAppSetting("todos", todos).catch(() => {});
  },
  addTodo: (item) => set((s) => {
    const todos = [{ ...item, id: Date.now(), completed: false, createdAt: new Date().toISOString() }, ...s.todos];
    localStorage.setItem("taxshop_mytodos", JSON.stringify(todos));
    db.upsertAppSetting("todos", todos).catch(() => {});
    toast.success("Added to your To-Do list");
    return { todos };
  }),
  toggleTodo: (id) => set((s) => {
    const todos = s.todos.map((t) => t.id === id ? { ...t, completed: !t.completed } : t);
    localStorage.setItem("taxshop_mytodos", JSON.stringify(todos)); db.upsertAppSetting("todos", todos).catch(() => {});
    return { todos };
  }),
  archiveTodo: (id) => set((s) => {
    const todos = s.todos.map((t) => t.id === id ? { ...t, archived: true, archivedAt: new Date().toISOString() } : t);
    localStorage.setItem("taxshop_mytodos", JSON.stringify(todos)); db.upsertAppSetting("todos", todos).catch(() => {});
    toast.success("Moved to archive");
    return { todos };
  }),
  unarchiveTodo: (id) => set((s) => {
    const todos = s.todos.map((t) => t.id === id ? { ...t, archived: false, archivedAt: null } : t);
    localStorage.setItem("taxshop_mytodos", JSON.stringify(todos)); db.upsertAppSetting("todos", todos).catch(() => {});
    return { todos };
  }),
  updateTodo: (id, patch) => set((s) => {
    const todos = s.todos.map((t) => t.id === id ? { ...t, ...patch } : t);
    localStorage.setItem("taxshop_mytodos", JSON.stringify(todos)); db.upsertAppSetting("todos", todos).catch(() => {});
    return { todos };
  }),
  removeTodo: (id) => set((s) => {
    const todos = s.todos.filter((t) => t.id !== id);
    localStorage.setItem("taxshop_mytodos", JSON.stringify(todos)); db.upsertAppSetting("todos", todos).catch(() => {});
    return { todos };
  }),

  // ── UI ──
  sidebarOpen: true,
  toggleSidebar: () => set((s) => ({ sidebarOpen: !s.sidebarOpen })),

  // ── Active timer ──
  activeTimer: (() => { try { return JSON.parse(localStorage.getItem("taxshop_active_timer")); } catch { return null; } })(),
  startTimer: (clientId, clientName, userName) => {
    const existing = get().activeTimer;
    if (existing) return; // guard: already running
    const timer = { clientId, clientName, userName, startAt: new Date().toISOString() };
    localStorage.setItem("taxshop_active_timer", JSON.stringify(timer));
    set({ activeTimer: timer });
  },
  stopTimer: async ({ description = "", isBillable = true } = {}) => {
    const timer = get().activeTimer;
    if (!timer) return;
    localStorage.removeItem("taxshop_active_timer");
    set({ activeTimer: null });
    const now = new Date();
    const durationSeconds = Math.round((now - new Date(timer.startAt)) / 1000);
    if (durationSeconds < 5) return;
    const entry = {
      id: `${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      clientId: timer.clientId,
      clientName: timer.clientName,
      userName: timer.userName,
      startAt: timer.startAt,
      endAt: now.toISOString(),
      durationSeconds,
      isBillable,
      description,
      date: now.toISOString().slice(0, 10),
    };
    const key = `time_log_${now.getFullYear()}_${String(now.getMonth() + 1).padStart(2, "0")}`;
    try {
      const existing = (await db.fetchAppSetting(key)) || [];
      await db.upsertAppSetting(key, [...existing, entry]);
    } catch {}
  },
  updateTimerEntry: async (entryId, patch) => {
    // Find which month key this entry lives in and patch it
    const now = new Date();
    for (let m = 0; m <= 6; m++) {
      const d = new Date(now.getFullYear(), now.getMonth() - m, 1);
      const key = `time_log_${d.getFullYear()}_${String(d.getMonth() + 1).padStart(2, "0")}`;
      try {
        const entries = (await db.fetchAppSetting(key)) || [];
        if (entries.some(e => e.id === entryId)) {
          await db.upsertAppSetting(key, entries.map(e => e.id === entryId ? { ...e, ...patch } : e));
          return;
        }
      } catch {}
    }
  },
  deleteTimerEntry: async (entryId) => {
    const now = new Date();
    for (let m = 0; m <= 6; m++) {
      const d = new Date(now.getFullYear(), now.getMonth() - m, 1);
      const key = `time_log_${d.getFullYear()}_${String(d.getMonth() + 1).padStart(2, "0")}`;
      try {
        const entries = (await db.fetchAppSetting(key)) || [];
        if (entries.some(e => e.id === entryId)) {
          await db.upsertAppSetting(key, entries.filter(e => e.id !== entryId));
          return;
        }
      } catch {}
    }
  },

  // ── Session cleanup ──
  resetStore: () => set({
    clients: [], tasks: [], deadlines: [], comms: [], notes: [],
    leads: [], engagementLetters: [], checkedItems: {}, uploadedDocs: [],
    activeClientName: null, activeTab: null, loading: false, error: null,
  }),
}));

export default useStore;
